import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { API_ENDPOINT } from '@core/configs';
import { formatDateTime } from '@core/utils';
import type {
  NewsDataDto,
  NewsItem,
  NewsPaginatedResponse,
  NewsResponseDto,
  UpdateNewsItemDto,
} from '../models/news.model';

@Injectable({
  providedIn: 'root',
})
export class NewsService {
  private readonly http = inject(HttpClient);
  // Cached for the session so list/detail/announcement share one request.
  // Every write below clears it so the public pages pick up admin edits.
  private cache?: NewsItem[];

  clearCache(): void {
    this.cache = undefined;
  }

  /**
   * Fetch every non-deleted news item, newest first. Admin needs unpublished
   * ones too, so filtering by publish flag happens in getPublished().
   */
  getAll(): Observable<NewsItem[]> {
    if (this.cache) {
      return of(this.cache);
    }
    return this.http.get<NewsResponseDto[]>(API_ENDPOINT.NEWS.GET_ALL).pipe(
      map((dtos) =>
        (dtos ?? [])
          .filter((dto) => (dto.delflg ?? 0) === 0)
          .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''))
          .map((dto) => this.normalizeNewsItem(dto)),
      ),
      tap((items) => (this.cache = items)),
    );
  }

  /**
   * Published-only feed backing every public entry point (home announcement,
   * news list, news detail).
   */
  private getPublished(): Observable<NewsItem[]> {
    return this.getAll().pipe(map((items) => items.filter((i) => i.statusCode === 'published')));
  }

  /** Client-side pagination over the published feed. */
  getNewsList(page = 1, limit = 5): Observable<NewsPaginatedResponse> {
    return this.getPublished().pipe(
      map((items) => {
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        return {
          items: items.slice(startIndex, endIndex),
          hasMore: endIndex < items.length,
          total: items.length,
        };
      }),
    );
  }

  /**
   * Published article by ID, or `null`. Served from the cache when it is filled;
   * otherwise fetched from GET /news/get-news/{newsId}.
   */
  getNewsById(id: string): Observable<NewsItem | null> {
    if (this.cache) {
      const cached = this.cache.find((item) => item.id === id);
      return of(cached && cached.statusCode === 'published' ? cached : null);
    }
    return this.http.get<NewsResponseDto>(API_ENDPOINT.NEWS.GET_NEWS(id)).pipe(
      map((dto) => {
        if (!dto || (dto.delflg ?? 0) !== 0) {
          return null;
        }
        const item = this.normalizeNewsItem(dto);
        return item.statusCode === 'published' ? item : null;
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * POST /news/create. The API defaults `publish` to false, so a new article
   * stays a draft until the publish switch is flipped.
   */
  create(data: NewsDataDto): Observable<NewsItem> {
    return this.http
      .post<NewsResponseDto>(API_ENDPOINT.NEWS.CREATE, { data: this.toWireData(data) })
      .pipe(
        tap(() => this.clearCache()),
        map((dto) => this.normalizeNewsItem(dto)),
      );
  }

  /**
   * Partial patch of one or more articles (POST /news/update). The API accepts 1-50 items and
   * writes only the `data` keys supplied, so a publish toggle sends `{ publish }` alone.
   */
  update(items: UpdateNewsItemDto[]): Observable<NewsItem[]> {
    const payload = items.map((item) => ({
      newsId: item.newsId,
      ...(item.data ? { data: this.toWireData(item.data) } : {}),
    }));
    return this.http.post<NewsResponseDto[]>(API_ENDPOINT.NEWS.UPDATE, { items: payload }).pipe(
      tap(() => this.clearCache()),
      map((dtos) => (dtos ?? []).map((dto) => this.normalizeNewsItem(dto))),
    );
  }

  /**
   * The API types `body` as a string while the editor holds a ProseMirror doc object.
   * parseContent() re-parses the JSON string on read, so the round-trip stays lossless.
   */
  private toWireData(data: NewsDataDto): NewsDataDto {
    if (data.body === undefined || typeof data.body === 'string') {
      return data;
    }
    return { ...data, body: JSON.stringify(data.body) };
  }

  normalizeNewsItem(dto: NewsResponseDto): NewsItem {
    const data = dto.data ?? {};

    return {
      id: dto.newsId,
      title: data.title?.trim() || '（無題）',
      content: data.body ?? '',
      author: data.author?.trim() || '管理者',
      date: formatDateTime(dto.createdAt, false),
      registeredAt: formatDateTime(dto.createdAt),
      updatedAt: dto.updatedAt ? formatDateTime(dto.updatedAt) : undefined,
      statusCode: data.publish === false ? 'unpublished' : 'published',
    };
  }
}
